import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Field,
  Grid,
  Inline,
  Panel,
  Spinner,
  Stack,
  useToast
} from "@leylion/design-system";
import {
  getOrganizationProfile,
  updateOrganizationProfile
} from "../api/client";
import type { OrganizationProfile } from "../api/settings-types";
import { formatDate } from "./settings-utils";

type Language = "ar" | "en";

export function OrganizationProfilePanel({
  language,
  onChanged
}: {
  language: Language;
  onChanged?: () => void;
}) {
  const toast = useToast();
  const [profile, setProfile] = useState<OrganizationProfile | null>(null);
  const [original, setOriginal] = useState<OrganizationProfile | null>(null);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState("");
  const ar = language === "ar";

  useEffect(() => {
    getOrganizationProfile()
      .then((result) => {
        setProfile(result);
        setOriginal(structuredClone(result));
      })
      .catch((caught: unknown) =>
        setError(caught instanceof Error ? caught.message : "Unable to load organization.")
      )
      .finally(() => setBusy(false));
  }, []);

  if (busy) {
    return (
      <Card>
        <Inline>
          <Spinner label={ar ? "جارٍ التحميل" : "Loading"} />
          <span>{ar ? "جارٍ تحميل ملف المؤسسة…" : "Loading organization profile…"}</span>
        </Inline>
      </Card>
    );
  }

  if (!profile) {
    return (
      <Alert tone="danger" title={ar ? "تعذر تحميل المؤسسة" : "Organization unavailable"}>
        {error}
      </Alert>
    );
  }

  const changed = JSON.stringify(profile) !== JSON.stringify(original);

  const update = <K extends keyof OrganizationProfile>(
    key: K,
    value: OrganizationProfile[K]
  ) => {
    setProfile((current) => (current ? { ...current, [key]: value } : current));
  };

  const save = async () => {
    setBusy(true);
    setError("");
    try {
      const saved = await updateOrganizationProfile(profile);
      setProfile(saved);
      setOriginal(structuredClone(saved));
      toast.notify({
        tone: "success",
        title: ar ? "تم حفظ ملف المؤسسة" : "Organization profile saved"
      });
      onChanged?.();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Unable to save organization.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack>
      <Panel
        title={ar ? "النشاط والهوية القانونية" : "Business & Legal Identity"}
        description={
          ar
            ? "مصدر الحقيقة للبيانات التي تظهر في الفواتير والإيصالات والتقارير القانونية."
            : "The source of truth for legal data shown on invoices, receipts and statutory reports."
        }
        actions={
          <Button type="button" disabled={busy || !changed} onClick={() => void save()}>
            {ar ? "حفظ التغييرات" : "Save changes"}
          </Button>
        }
      >
        <Stack>
          {error ? (
            <Alert tone="danger" title={ar ? "تعذر الحفظ" : "Save failed"}>
              {error}
            </Alert>
          ) : null}

          <Card title={ar ? "البيانات الأساسية" : "Core details"}>
            <Grid columns={2}>
              <Field
                label={ar ? "الاسم التجاري" : "Trade name"}
                required
                value={profile.tradeName}
                onChange={(event) => update("tradeName", event.target.value)}
              />
              <Field
                label={ar ? "الاسم القانوني" : "Legal name"}
                required
                value={profile.legalName}
                onChange={(event) => update("legalName", event.target.value)}
              />
              <Field
                label={ar ? "رقم الرخصة" : "License number"}
                value={profile.licenseNumber ?? ""}
                onChange={(event) => update("licenseNumber", event.target.value || null)}
              />
              <Field
                label={ar ? "الرقم الضريبي TRN" : "Tax registration number"}
                value={profile.taxRegistrationNumber ?? ""}
                onChange={(event) =>
                  update("taxRegistrationNumber", event.target.value || null)
                }
              />
              <Field
                label={ar ? "رمز الدولة" : "Country code"}
                required
                maxLength={2}
                value={profile.countryCode}
                onChange={(event) => update("countryCode", event.target.value.toUpperCase())}
              />
              <Field
                label={ar ? "رابط الشعار" : "Logo URL"}
                type="url"
                value={profile.logoUrl ?? ""}
                onChange={(event) => update("logoUrl", event.target.value || null)}
              />
            </Grid>
          </Card>

          <Card title={ar ? "التواصل" : "Contact"}>
            <Grid columns={2}>
              <Field
                label={ar ? "البريد الإلكتروني" : "Email"}
                type="email"
                value={profile.email ?? ""}
                onChange={(event) => update("email", event.target.value || null)}
              />
              <Field
                label={ar ? "الهاتف" : "Phone"}
                type="tel"
                value={profile.phone ?? ""}
                onChange={(event) => update("phone", event.target.value || null)}
              />
              <Field
                label={ar ? "الموقع الإلكتروني" : "Website"}
                type="url"
                value={profile.website ?? ""}
                onChange={(event) => update("website", event.target.value || null)}
              />
            </Grid>
          </Card>

          <Card title={ar ? "العنوان القانوني" : "Legal address"}>
            <Grid columns={2}>
              <Field
                label={ar ? "العنوان الأول" : "Address line 1"}
                value={profile.addressLine1 ?? ""}
                onChange={(event) => update("addressLine1", event.target.value || null)}
              />
              <Field
                label={ar ? "العنوان الثاني" : "Address line 2"}
                value={profile.addressLine2 ?? ""}
                onChange={(event) => update("addressLine2", event.target.value || null)}
              />
              <Field
                label={ar ? "المدينة" : "City"}
                value={profile.city ?? ""}
                onChange={(event) => update("city", event.target.value || null)}
              />
              <Field
                label={ar ? "الإمارة / المنطقة" : "Region"}
                value={profile.region ?? ""}
                onChange={(event) => update("region", event.target.value || null)}
              />
              <Field
                label={ar ? "الرمز البريدي" : "Postal code"}
                value={profile.postalCode ?? ""}
                onChange={(event) => update("postalCode", event.target.value || null)}
              />
            </Grid>
          </Card>

          <p className="settings-meta">
            {ar ? "آخر تحديث" : "Last updated"}: {formatDate(profile.updatedAt, language)}
            {" · "}
            {ar ? "الإصدار" : "Version"} {profile.version}
          </p>
        </Stack>
      </Panel>
    </Stack>
  );
}
